<script setup lang="js">
// JSDoc types for editor hints (optional)
/** @typedef {import('@tiptap/vue-3').Editor} Editor */

import { TextSelection } from '@tiptap/pm/state'
import { BubbleMenu } from '@tiptap/vue-3/menus'
import { computed, unref } from 'vue'
import { useLocale } from '@/tiptap/locales/index.js'
import { isExtEnableAndActive } from '@/tiptap/utils/utils'

// Runtime props (no TS)
const props = defineProps({
  /** @type {Editor} */
  editor: { type: Object, required: true }
})


const { t } = useLocale()

const tippyOpts = {
  // Let Popper pick the best side automatically, we’ll guide it with modifiers
  placement: 'auto',
  appendTo: () => document.body,     // escape stacking/overflow contexts
  zIndex: 3000,                      // above sticky toolbar
  offset: [0, 8],                    // small gap from selection
  strategy: 'fixed',                 // position relative to viewport (more stable in dialogs/sticky headers)
  popperOptions: {
    modifiers: [
      // Keep the menu inside the viewport; treat the top as having virtual padding
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          rootBoundary: 'viewport',
          altBoundary: true,
          tether: true,
          padding: { top: 72, bottom: 8, left: 8, right: 8 }, // ↑ toolbar height budget
        },
      },
      // Allow flipping; prefer bottom if top is obstructed by the sticky toolbar
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'top', 'right', 'left'],
          boundary: 'viewport',
          padding: 8,
        },
      },
      // After flipping, shift horizontally to stay on-screen
      {
        name: 'shift',
        options: {
          boundary: 'viewport',
          padding: 8,
        },
      },
    ],
  },
}

// Determine current node type under selection to decide which bubble menu to show
const nodeType = computed(() => {
  const selection = props.editor.state.selection
  if (!selection || selection.to === selection.from || selection.empty) return undefined

  const isLink = isExtEnableAndActive(props.editor, 'link')
  const isTable = isExtEnableAndActive(props.editor, 'table')

  const isImage = selection.node?.type?.name === 'image'
  const isVideo = selection.node?.type?.name === 'video'
  const isText = selection instanceof TextSelection

  if (isTable) return 'table'
  if (isLink) return 'link'
  if (isImage) return 'image'
  if (isVideo) return 'video'
  if (isText) return 'text'
  return undefined
})

function getMenus(nodeKey) {
  if (!nodeKey) return []

  const { extensions = [] } = props.editor.extensionManager || {}
  const find = extensions.find(k => k.name === 'base-kit')
  if (!find) return []

  const { button } = (find.options && find.options.bubble) || {}
  if (!button) return []

  const _buttons = button({
    editor: props.editor,
    extension: find,
    t: unref(t)
  })

  return (_buttons && _buttons[nodeKey]) || []
}
</script>

<template>
  <BubbleMenu :editor="editor" :tippy-options="tippyOpts">
    <VCard class="vuetify-pro-tiptap-editor__menu-bubble">
      <VCardText class="d-flex pa-0">
        <VToolbar density="compact" flat height="auto" class="py-1 ps-1">
          <template v-for="(item, key) in getMenus(nodeType)" :key="key">
            <!-- Divider -->
            <VDivider v-if="item.type === 'divider'" vertical class="mx-1 me-2" />
            <!-- Buttons -->
            <component
              :is="item.component"
              v-else
              v-bind="item.componentProps"
              :editor="editor"
            >
              <template v-for="(element, slotName, i) in item.componentSlots" :key="i" #[`${slotName}`]="values">
                <component :is="element" v-bind="values?.props" />
              </template>
            </component>
          </template>
        </VToolbar>
      </VCardText>
    </VCard>
  </BubbleMenu>
</template>

<style scoped>
.vuetify-pro-tiptap-editor__menu-bubble {
  z-index: 3000;
}
</style>